import React from 'react';
import { motion } from 'framer-motion';
import OptimizedImage from './OptimizedImage';


interface ChatMessageProps {
  message: string;
  sender: 'user' | 'bot';
  timestamp?: string;
  avatar?: string;
  isTyping?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({
  message,
  sender,
  timestamp,
  avatar,
  isTyping = false,
}) => {
  const isUser = sender === 'user';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex gap-3 mb-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      {/* Avatar */}
      <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center ${
        isUser 
          ? 'bg-gradient-to-r from-accent-coral to-accent-peach' 
          : 'bg-gradient-to-r from-accent-mint to-accent-lavender'
      }`}>
        {avatar ? (
          <OptimizedImage 
            src={avatar} 
            alt="Avatar" 
            className="w-full h-full rounded-full"
            placeholder="👤"
            priority={true}
          />
        ) : (
          <span className="text-white font-semibold text-sm">
            {isUser ? 'U' : 'AI'}
          </span>
        )}
      </div>

      {/* Message Content */}
      <div className={`max-w-xs lg:max-w-md ${isUser ? 'items-end' : 'items-start'} flex flex-col`}>
        <div className={`px-4 py-3 rounded-2xl ${
          isUser
            ? 'bg-accent-lightPink text-black rounded-br-md'
            : 'glass-effect border border-dark-600 text-white rounded-bl-md'
        }`}>
          {isTyping ? (
            <div className="flex space-x-1">
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.5, 1, 0.5],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: i * 0.2,
                  }}
                  className="w-2 h-2 bg-accent-mint rounded-full"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm leading-relaxed">{message}</p>
          )}
        </div>
        
        {timestamp && (
          <span className="text-xs text-dark-400 mt-1 px-2">
            {timestamp}
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default ChatMessage;